import React, { lazy, Suspense, useEffect } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useLocation, useNavigation } from 'react-router-dom';

import { AdminInfo, InviteToYatModal, Web3AuthContextProvider, Web3AuthPanel } from '@pkg/components';
import YatLoader from '@pkg/components/Sections/Profile/Loader';
import { ToastStack } from '@pkg/components/Toast/ToastStack';
import { useAppSelector, useInviteModal } from '@pkg/hooks';
import WebGLLayer from '@pkg/webgl/WebGLLayer';

import { MarketplaceOverlays, MintingOverlays, WalletOverlays } from '@app/features';
import { track_page_view, waitUntilAnalyticsIsReady } from '@app/helpers';
import { NavigationState } from '@app/routes/types';
import { useAuthSidebar } from '@app/store/zustand';

import { TwoFAPopups } from './components/TwoFAPopups';

const Notifications = lazy(() => import('@pkg/components/Elements/Notification'));
const YatErrorPopup = lazy(() =>
    import('@pkg/components/ErrorPopup/ErrorPopup').then((module) => ({ default: module.YatErrorPopup })),
);

export function RoutesWrapper() {
    const navigation = useNavigation();
    const pathname = useLocation().pathname;

    const { open: showInviteModal } = useInviteModal();
    const { isAuthSidebarOpen, closeAuthSidebar, preventClose } = useAuthSidebar();

    const marketplaceV1Enabled = useAppSelector((state) => state.featureFlags.features.marketplaceV1Enabled);

    useEffect(() => {
        if (window.dataLayer) {
            window.dataLayer.push({ event: 'optimize.activate' });
        }

        if (typeof window.analytics?.user === 'function') {
            track_page_view(pathname);
        } else {
            waitUntilAnalyticsIsReady(async () => {
                await track_page_view(pathname);
            });
        }
    }, [pathname]);

    const inviteModalMarkup = showInviteModal ? <InviteToYatModal /> : null;

    return (
        <>
            <Suspense fallback={<div />}>
                {navigation.state === NavigationState.Loading && <YatLoader />}

                <Notifications />
                <YatErrorPopup />
                <ToastStack />

                <main>
                    <WebGLLayer />
                </main>

                {inviteModalMarkup}

                <TwoFAPopups />
                <AdminInfo />

                {isAuthSidebarOpen && (
                    <Web3AuthContextProvider>
                        <AnimatePresence>
                            <Web3AuthPanel onClose={preventClose ? undefined : closeAuthSidebar} />
                        </AnimatePresence>
                    </Web3AuthContextProvider>
                )}

                {marketplaceV1Enabled ? <MarketplaceOverlays /> : null}
                <MintingOverlays />
                <WalletOverlays />
            </Suspense>
        </>
    );
}
